import { useCallback, useEffect, useRef, useState } from "react";
import { useOrderDraft } from "../../context/OrderDraftContext";
import { useSession } from "../../context/SessionContext";
import { useApi } from "../../hooks/useApi";
import { useKitRecommendation } from "../kits/useKitRecommendation";
import { packagesService } from "../../services/packages";
import { ApiOpmeKit, Kit, OpmeItem } from "../../types/order.types";
import { opmeKitFromApi, opmeToApi } from "../../lib/mappers";
import { CUSTOM } from "../procedures/useProcedureKits";
import { OpmeSave } from "./useOpmeSave";
import { notify } from "../../components/ui/Toast";
import { t } from "../../i18n";

// Pacotes de OPME: só busca com a aba aberta; a IA só recomenda quando o médico pede (recommend).
export const useOpmeKits = (active: boolean, save: OpmeSave) => {
  const {
    newInputRequest, laudoText, setOpmeKit, selectedButtonOpmeKits,
    selectedOpmeKitsType, setSelectedOpmeKitsType, selectedOpmeKitIndex, setSelectedOpmeKitIndex,
  } = useOrderDraft();
  const { metadata } = useSession();
  const { request, apiFetch } = useApi<ApiOpmeKit[]>();
  const [kits, setKits] = useState<Kit<OpmeItem>[] | null>(null);
  const createdName = useRef<string | null>(null);
  const [recommending, setRecommending] = useState(false);

  const custom = selectedOpmeKitsType === CUSTOM;
  const { remember, mark } = useKitRecommendation(`opme:${selectedOpmeKitsType}`);

  // recomendar = o médico pediu a IA: a lista continua na tela enquanto ela escolhe.
  const load = useCallback(async (recomendar = false) => {
    if (!recomendar) setKits(null);
    const body = { historico: newInputRequest, laudo: laudoText, recomendar, ...(custom ? { email: metadata.email } : { especialidade: metadata.especialidade }) };
    const data = await request(() => packagesService.opmeKits(apiFetch, custom ? "personalizados" : "padronizados", body));
    if (recomendar && !data) return;
    const list = (data ?? []).map(opmeKitFromApi);
    setKits(list);
    // Sem reordenar: o pedido salvo guarda a posição do pacote nesta lista.
    // Fica escolhido o pacote recém-criado ou o que a IA acabou de recomendar; senão, o que já estava.
    const created = list.findIndex((k) => k.name === createdName.current);
    createdName.current = null;
    const ai = recomendar ? list.findIndex((k) => k.recommended) : -1;
    if (ai >= 0) remember(list[ai].id);
    else if (recomendar) notify({ tone: "info", title: t("kits.noRecommendation"), body: t("kits.noRecommendationBody") });
    setSelectedOpmeKitIndex((i: number) => (created >= 0 ? created : ai >= 0 ? ai : i));
  }, [remember, request, apiFetch, custom, newInputRequest, laudoText, metadata.email, metadata.especialidade, setSelectedOpmeKitIndex]);

  useEffect(() => {
    if (active) load();
  }, [active, load]);

  const recommend = async () => {
    setRecommending(true);
    await load(true);
    setRecommending(false);
  };

  const selectedKit = kits?.[selectedOpmeKitIndex] ?? null;

  const changeType = (type: string) => {
    if (type === selectedOpmeKitsType) return;
    setSelectedOpmeKitsType(type);
    setSelectedOpmeKitIndex(-1);
  };

  // O pacote novo é personalizado: abre essa aba já atualizada e com ele escolhido.
  const showCreated = (name: string) => {
    createdName.current = name;
    if (custom) load();
    else changeType(CUSTOM);
  };

  const confirm = async () => {
    if (!selectedKit) return;
    const rows = selectedKit.items.map(opmeToApi);
    setOpmeKit(rows);
    await save(rows, { tipo: selectedButtonOpmeKits, tipoDoPacote: selectedOpmeKitsType, indice: selectedOpmeKitIndex }, true);
  };

  return {
    kits: kits && mark(kits),
    recommend,
    recommending,
    type: selectedOpmeKitsType,
    custom,
    changeType,
    selected: selectedOpmeKitIndex,
    select: setSelectedOpmeKitIndex,
    selectedKit,
    showCreated,
    confirm,
  };
};
